import { useCallback, useState, lazy, Suspense } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../common/hooks"
import { PageHeader, ApprovedUrlsModal } from "../../common/ui"
import {
  IntegrationsTable,
  IntegrationsPageHeaderActions
} from "../../common/ui/integrations"
import { setPreviewModal } from "../../redux/slices/integrations"
import { selectUserDetails, selectUserRoles } from "../../redux/slices/auth"
import { ALLOWED_ROLES_BY_ROUTE } from "../../common/constants"
import { RequireRole } from "../../common/validation"
import { checkIsAdmin, checkIsSuperAdmin } from "../../common/utils"
import { useIntegrationsPageData } from "./use-integrations-page-data"

const PreviewModal = lazy(() => import("../../common/ui/preview-modal"))

const IntegrationsPage = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const userRoles = useAppSelector(selectUserRoles)
  const user = useAppSelector(selectUserDetails)

  const isAdmin = checkIsAdmin(userRoles)
  const isSuperAdmin = checkIsSuperAdmin(userRoles)

  const [selectedBrandId, setSelectedBrandId] = useState(
    searchParams.get("brand_id") ?? ""
  )

  const { brands, todayIntegrations, integrations, isLoadingIntegrations } =
    useIntegrationsPageData({
      isSuperAdmin,
      orgId: user?.org?.id,
      selectedBrandId
    })

  const onSelectedBrandChange = useCallback(
    (brandId: string) => {
      setSelectedBrandId(brandId)
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        if (brandId) next.set("brand_id", brandId)
        else next.delete("brand_id")
        return next
      })
    },
    [setSearchParams]
  )

  const [approvedUrlsModalOpen, setApprovedUrlsModalOpen] = useState(false)

  const handleSelectIntegration = useCallback(
    (nextId: string) => {
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search
      )
      navigate(`/integrations/${nextId}?returnUrl=${returnUrl}`)
    },
    [navigate]
  )

  const onClickNew = useCallback(() => {
    navigate("/integrations/new")
  }, [navigate])

  const onOpenApprovedUrls = useCallback(
    () => setApprovedUrlsModalOpen(true),
    []
  )

  const onClosePreviewModal = useCallback(() => {
    dispatch(setPreviewModal({ isOpen: false, appKey: "" }))
  }, [dispatch])

  return (
    <RequireRole allowedRoles={ALLOWED_ROLES_BY_ROUTE.integrations}>
      <>
        <div className="min-h-full p-6 bg-white">
          <PageHeader title="Integrations">
            <IntegrationsPageHeaderActions
              isAdmin={isAdmin}
              onOpenApprovedUrls={onOpenApprovedUrls}
              onClickNew={onClickNew}
            />
          </PageHeader>

          <IntegrationsTable
            todayIntegrations={todayIntegrations}
            integrations={integrations}
            brands={brands}
            selectedBrandId={selectedBrandId}
            onSelectedBrandChange={onSelectedBrandChange}
            isLoading={isLoadingIntegrations}
            onSelect={handleSelectIntegration}
          />
        </div>

        <ApprovedUrlsModal
          show={approvedUrlsModalOpen}
          toggleModal={setApprovedUrlsModalOpen}
        />

        <Suspense fallback={<div>Loading...</div>}>
          <PreviewModal onClose={onClosePreviewModal} />
        </Suspense>
      </>
    </RequireRole>
  )
}

export default IntegrationsPage
